import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import PatientAvatar from "./PatientAvatar";
import CollapsibleSection, { CollapseToggle } from "./CollapsibleSection";
import PatientStaffScheduleSection from "./staffSchedule/PatientStaffScheduleSection";
import { UserPlusIcon } from "../../../Icons";
import {
  getAvailableStaff,
  assignStaff,
  removeStaff,
} from "../../../services/patientService";
import {
  PATIENT_SECTION_TONE,
  PATIENT_ROLE_TONE,
  PATIENT_THEME,
} from "../patientTheme";

/** Flat role order — rendered 3 per row. */
const ROLE_ORDER = [
  "RN",
  "LVN",
  "CG",
  "CNA",
  "PT",
  "PTA",
  "OT",
  "OTA",
  "ST",
  "HHA",
  "MSW",
  "CON",
  "SRVY",
];

const ROLE_LABELS = {
  RN: "RN",
  LVN: "LVN",
  CG: "CG",
  CNA: "CNA",
  PT: "PT",
  PTA: "PTA",
  OT: "OT",
  OTA: "OTA",
  ST: "ST",
  HHA: "HHA",
  MSW: "MSW",
  CON: "Contract",
  SRVY: "Surveyor",
};

const ROLE_TONES = new Proxy(
  {},
  {
    get: () => PATIENT_ROLE_TONE,
  },
);

const DEFAULT_TONE = PATIENT_ROLE_TONE;
const CARE_TEAM_TONE = PATIENT_SECTION_TONE;

const roleLabel = (role) => ROLE_LABELS[role] || role;

const normalizeChartId = (chartId) => {
  if (chartId == null || chartId === "") return undefined;
  const n = Number(chartId);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

const ActionButton = ({ assigned, loading, onClick, orphan }) => (
  <button
    type="button"
    disabled={loading}
    onClick={onClick}
    title={
      assigned
        ? orphan
          ? "Inactive / mismatched role — click to remove"
          : "Click to remove"
        : "Assign to care team"
    }
    className={`shrink-0 inline-flex items-center justify-center h-6 min-w-[3.75rem] px-1.5 text-[10px] font-semibold rounded border transition-colors disabled:opacity-50 ${
      assigned
        ? orphan
          ? "bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100"
          : "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
        : `bg-white ${PATIENT_THEME.button.outline}`
    }`}
  >
    {loading ? (
      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
    ) : assigned ? (
      orphan ? (
        "Remove"
      ) : (
        "Assigned"
      )
    ) : (
      "Assign"
    )}
  </button>
);

const RoleCard = ({ group, canAssign, actionLoading, onToggle }) => {
  const tone = ROLE_TONES[group.role] || DEFAULT_TONE;
  const [open, setOpen] = useState(true);

  return (
    <div
      className={`rounded-lg border overflow-hidden flex flex-col shadow-sm ${tone.card} ${tone.soft}`}
    >
      <div
        className={`flex items-center justify-between gap-1.5 px-2 py-1.5 border-b ${tone.head}`}
      >
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <CollapseToggle
            open={open}
            onToggle={() => setOpen((o) => !o)}
            size="sm"
            labelOpen={`Collapse ${roleLabel(group.role)}`}
            labelClosed={`Expand ${roleLabel(group.role)}`}
          />
          <span className={`w-0.5 h-3.5 rounded-full shrink-0 ${tone.bar}`} />
          <h3
            className={`text-[11px] font-bold uppercase tracking-wide truncate ${tone.title}`}
          >
            {roleLabel(group.role)}
          </h3>
          {group.assignedCount > 0 ? (
            <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 rounded shrink-0">
              {group.assignedCount}
            </span>
          ) : null}
        </div>
      </div>

      {open ? (
        <ul className="divide-y divide-slate-100/80 max-h-44 overflow-y-auto bg-white">
          {!group.members?.length ? (
            <li className="px-2 py-3 text-[11px] text-slate-500 text-center">
              No staff
            </li>
          ) : (
            group.members.map((member) => {
              const employeeId = member.employeeId ?? member.id;
              const busyKey = `${group.role}-${employeeId}`;
              return (
                <li
                  key={`${group.role}-${employeeId}`}
                  className={`flex items-center justify-between gap-1.5 px-2 py-1.5 ${
                    member.isAssigned
                      ? member.isOrphan
                        ? "bg-amber-50/70"
                        : "bg-emerald-50/60"
                      : "bg-white"
                  }`}
                >
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <PatientAvatar
                      firstName={member.firstName}
                      lastName={member.lastName}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium text-slate-900 truncate leading-tight">
                        {member.firstName} {member.lastName}
                      </p>
                      {member.isOrphan ? (
                        <p className="text-[9px] text-amber-700 truncate leading-tight">
                          Not in active {roleLabel(group.role)} list
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {canAssign ? (
                    <ActionButton
                      assigned={member.isAssigned}
                      orphan={Boolean(member.isOrphan)}
                      loading={actionLoading === busyKey}
                      onClick={() => onToggle(member, group.role)}
                    />
                  ) : member.isAssigned ? (
                    <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded shrink-0">
                      On team
                    </span>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
};

const emptyGroup = (role) => ({
  role,
  availableCount: 0,
  assignedCount: 0,
  members: [],
});

const PatientStaffTab = ({
  patientId,
  chartId,
  assignedStaff,
  canAssign,
  onRefresh,
}) => {
  const [staffGroups, setStaffGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);
  const scopedChartId = normalizeChartId(chartId);

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      if (canAssign) {
        const res = await getAvailableStaff(
          patientId,
          scopedChartId ? { chartId: scopedChartId } : {},
        );
        setStaffGroups(res.result?.staffGroups || []);
      } else {
        setStaffGroups([]);
      }
    } catch (err) {
      toast.error(err.message || "Failed to load staff");
    } finally {
      setLoading(false);
    }
  }, [patientId, scopedChartId, canAssign]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const groupByRole = useMemo(() => {
    const map = {};
    for (const g of staffGroups) map[g.role] = g;
    return map;
  }, [staffGroups]);

  const handleToggle = async (member, role) => {
    const employeeId = Number(member.employeeId ?? member.id);
    if (!Number.isFinite(employeeId) || employeeId <= 0) {
      toast.error("Invalid staff member id");
      return;
    }

    const busyKey = `${role}-${employeeId}`;
    if (actionLoading) return;
    setActionLoading(busyKey);

    const chartPayload = scopedChartId ? { chartId: scopedChartId } : {};

    const applyLocal = (nextAssigned, nextAssignmentId = null) => {
      setStaffGroups((groups) =>
        groups.map((g) => {
          if (g.role !== role) return g;
          const members = g.members.map((m) => {
            const mid = Number(m.employeeId ?? m.id);
            if (mid !== employeeId) return m;
            return {
              ...m,
              isAssigned: nextAssigned,
              assignmentId: nextAssigned ? nextAssignmentId : null,
              isOrphan: nextAssigned ? m.isOrphan : false,
            };
          });
          return {
            ...g,
            members,
            assignedCount: members.filter((m) => m.isAssigned).length,
          };
        }),
      );
    };

    try {
      if (member.isAssigned) {
        const assignmentId =
          member.assignmentId ||
          assignedStaff.find(
            (a) =>
              Number(a.employeeId) === employeeId && String(a.role) === role,
          )?.assignmentId;
        if (!assignmentId) {
          toast.error("Assignment not found — refreshing list");
          await loadStaff();
          return;
        }
        await removeStaff(patientId, assignmentId);
        applyLocal(false, null);
        toast.success("Staff removed");
      } else {
        const res = await assignStaff(patientId, {
          employeeId,
          role,
          ...chartPayload,
        });
        const assignment = res?.result?.assignment || res?.assignment || null;
        applyLocal(true, assignment?.assignmentId ?? assignment?.id ?? null);
        toast.success("Staff assigned");
      }

      await loadStaff();
      setScheduleRefreshKey((k) => k + 1);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || "Action failed");
      await loadStaff();
    } finally {
      setActionLoading(null);
    }
  };

  const totalAssigned = canAssign
    ? staffGroups.reduce((sum, g) => sum + (g.assignedCount || 0), 0)
    : (assignedStaff?.length ?? 0);

  return (
    <div className="space-y-3">
      <CollapsibleSection
      title="Care Team"
      icon={<UserPlusIcon size={14} />}
      tone={CARE_TEAM_TONE}
      defaultOpen
      actions={
        canAssign ? (
          <p className="text-[10px] text-slate-500 hidden sm:block">
            Assign multiple staff per role · click Assigned to remove
          </p>
        ) : null
      }
    >
      <div className="p-3">
        {!canAssign ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
            {totalAssigned === 0 ? (
              <div className="col-span-full border border-dashed border-slate-200 rounded-lg py-8 text-center bg-white">
                <p className="text-sm text-slate-500">No staff assigned yet</p>
              </div>
            ) : (
              assignedStaff.map((member) => {
                const tone = ROLE_TONES[member.role] || DEFAULT_TONE;
                return (
                  <div
                    key={member.assignmentId}
                    className={`rounded-lg border bg-white overflow-hidden shadow-sm ${tone.card}`}
                  >
                    <div
                      className={`flex items-center gap-1.5 px-2 py-1.5 border-b ${tone.head}`}
                    >
                      <span
                        className={`w-0.5 h-3 rounded-full shrink-0 ${tone.bar}`}
                      />
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide ${tone.title}`}
                      >
                        {roleLabel(member.role)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-2">
                      <PatientAvatar
                        firstName={member.firstName}
                        lastName={member.lastName}
                        size="sm"
                      />
                      <p className="text-[11px] font-medium text-slate-900 truncate min-w-0">
                        {member.firstName} {member.lastName}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : loading ? (
          <div className="rounded-lg border border-slate-200 bg-white py-10 text-center text-sm text-slate-400 animate-pulse">
            Loading care team...
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
            {ROLE_ORDER.map((role) => (
              <RoleCard
                key={role}
                group={groupByRole[role] || emptyGroup(role)}
                canAssign={canAssign}
                actionLoading={actionLoading}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}
      </div>
    </CollapsibleSection>

      <PatientStaffScheduleSection
        patientId={patientId}
        chartId={scopedChartId}
        canEdit={canAssign}
        refreshKey={scheduleRefreshKey}
      />
    </div>
  );
};

export default PatientStaffTab;
